import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import {
  BANNER_HEADER_OFFSET_CHANGE_EVENT,
  getBannerOffset
} from '@/components/Banner/bannerHeaderOffset';
import { useParticipant } from '@/providers/ParticipantProvider';

type HandleResizeProps = {
  left: number;
  top: number;
  width: number;
  height: number;
  ref: MutableRefObject<HTMLDivElement | null>;
  maxHeight?: number;
  reset?: boolean;
};

type Props = {
  isOpen: boolean;
  isBig: boolean;
  isHeaderFixed: boolean;
  expandTargetId?: string;
};

const DROPDOWN_OFFSET = 10;
const DROPDOWN_BOTTOM_MARGIN = 10;
const DEFAULT_Z_INDEX_CLOSED = 2;
const DEFAULT_Z_INDEX_OPENED = 5;

export const useSearchInputPosition = ({
  isOpen,
  isBig,
  expandTargetId,
  isHeaderFixed = false
}: Props) => {
  const spaceBarRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const scrollPosition = useRef<number>(0);
  const { participant } = useParticipant();

  const getActiveElementRect = (): DOMRect | null => {
    return (
      spaceBarRef.current?.getBoundingClientRect() ||
      searchInputRef.current?.getBoundingClientRect() ||
      null
    );
  };

  const calculateDropdownPosition = (
    rect: DOMRect,
    isFixedPosition: boolean,
    topFallback: number = 0
  ): { inputTop: number; dropdownTop: number; maxHeight: number } => {
    const computedScroll = isFixedPosition ? 0 : window.scrollY;
    const inputTop = (rect.top ?? topFallback) + computedScroll;
    const dropdownTop = inputTop + rect.height + DROPDOWN_OFFSET;

    const maxHeight =
      computedScroll +
      window.innerHeight -
      dropdownTop -
      DROPDOWN_BOTTOM_MARGIN;

    return { inputTop, dropdownTop, maxHeight };
  };

  const targetButton = expandTargetId
    ? document.getElementById(expandTargetId)
    : null;

  const calculateExpandedWidth = useCallback(
    (targetElement: HTMLElement): number => {
      const targetElementRect = targetElement.getBoundingClientRect();

      const parentElement = searchInputRef.current?.parentElement;
      const parentElementRect = parentElement?.getBoundingClientRect();

      const targetElementRightPosition = targetElementRect.right ?? 0;
      const searchInputLeftPosition = parentElementRect?.left ?? 0;

      return targetElementRightPosition - searchInputLeftPosition;
    },
    [targetButton]
  );

  const handleLockScroll = (isOpened: boolean, disableScroll: boolean) => {
    const body = document.querySelector('body');
    if (!body) return;

    const styles = {
      bottom: 0,
      left: 0,
      right: 0,
      top: `${scrollPosition.current * -1}px`,
      width: '100%',
      position: 'fixed'
    };

    if (disableScroll)
      return Object.keys(styles).forEach(key => body.style.removeProperty(key));

    if (isOpened) {
      scrollPosition.current =
        document.documentElement.scrollTop || window.pageYOffset;

      Object.entries(styles).forEach(([key, value]) => {
        body.style.setProperty(key, `${value}`);
      });
      return;
    }

    Object.keys(styles).forEach(key => body.style.removeProperty(key));
    window.scrollTo(0, scrollPosition.current);
  };

  const applyPositionStyles = useCallback(
    ({ left, top, width, ref, maxHeight }: HandleResizeProps) => {
      const element = ref?.current;
      if (!element) return;

      const bannerOffset = isHeaderFixed ? getBannerOffset() : 0;
      const topWithBanner = top + bannerOffset;

      const baseStyles = 'top: 0; left: 0;';
      const positionType = isHeaderFixed ? 'fixed' : 'absolute';
      const zIndex = isOpen ? DEFAULT_Z_INDEX_OPENED : DEFAULT_Z_INDEX_CLOSED;

      const dynamicStyles = [
        `position: ${positionType}`,
        `transform: translate(${left}px, ${topWithBanner}px)`,
        `width: ${width}px`,
        `z-index: ${zIndex}`,
        maxHeight ? `max-height: ${maxHeight}px` : ''
      ]
        .filter(Boolean)
        .join('; ');

      element.setAttribute('style', `${baseStyles} ${dynamicStyles}`);
    },
    [isOpen, isHeaderFixed]
  );

  const applyFixedHeaderInputStyles = (targetElement: HTMLElement): void => {
    const element = searchInputRef.current;
    if (!element || !targetElement) return;

    const bannerOffset = getBannerOffset();
    const parentElement = searchInputRef.current?.parentElement;
    const parentElementRect = parentElement?.getBoundingClientRect();
    const targetElementRect = targetElement.getBoundingClientRect();

    const searchInputLeftPosition = parentElementRect?.left ?? 0;
    const targetElementRightPosition = targetElementRect.right ?? 0;

    const openedWidth = targetElementRightPosition - searchInputLeftPosition;
    const closedWidth = parentElementRect?.width ?? 0;
    const inputWidth = isOpen ? openedWidth : closedWidth;

    const { style } = element;
    style.position = 'fixed';
    style.zIndex = '5';
    style.left = `${searchInputLeftPosition}px`;
    style.top = `${targetElementRect.top + bannerOffset}px`;
    style.width = `${inputWidth}px`;
    style.height = `${targetElementRect.height}px`;
  };

  const handleResetStyles = useCallback(
    ({ ref }: Pick<HandleResizeProps, 'ref'>) => {
      const element = ref.current;
      if (!element) return;

      const defaultStyles = 'top: 0;\nleft: 0;';
      element.setAttribute('style', `${defaultStyles} `);
    },
    []
  );

  const updateElementPositions = useCallback(() => {
    const rect = getActiveElementRect();
    if (!rect) return handleResetStyles({ ref: dropdownRef });

    const initialTop = spaceBarRef.current?.getBoundingClientRect()?.top ?? 0;

    const { inputTop, dropdownTop, maxHeight } = calculateDropdownPosition(
      rect,
      isHeaderFixed,
      initialTop
    );

    const baseResizeProps = {
      height: rect.height,
      width: rect.width,
      top: inputTop,
      left: rect.left,
      ref: searchInputRef
    };

    if (spaceBarRef.current) {
      applyPositionStyles(baseResizeProps);
    }

    const dropdownProps = {
      ...baseResizeProps,
      top: dropdownTop,
      maxHeight,
      ref: dropdownRef
    };

    if (expandTargetId && targetButton) {
      const expandedWidth = calculateExpandedWidth(targetButton);
      dropdownProps.width = expandedWidth;
    }

    if (isBig) {
      applyPositionStyles(dropdownProps);
    } else {
      handleResetStyles({ ref: dropdownRef });
    }
  }, [
    targetButton,
    isHeaderFixed,
    isBig,
    expandTargetId,
    applyPositionStyles,
    handleResetStyles,
    spaceBarRef.current
  ]);

  const updateFixedHeaderInput = useCallback(() => {
    if (!isHeaderFixed || !searchInputRef.current || !targetButton) return;
    applyFixedHeaderInputStyles(targetButton);
  }, [isHeaderFixed, searchInputRef, targetButton, expandTargetId, isOpen]);

  const handleAllPositionUpdates = useCallback(() => {
    updateElementPositions();
    updateFixedHeaderInput();
  }, [updateElementPositions, updateFixedHeaderInput]);

  useEffect(() => {
    let rafId: number | null = null;

    const handleResize = () => {
      if (rafId) cancelAnimationFrame(rafId);

      rafId = requestAnimationFrame(handleAllPositionUpdates);
    };

    handleResize();
    window.addEventListener('scroll', handleResize);
    window.addEventListener('resize', handleResize);
    window.addEventListener(BANNER_HEADER_OFFSET_CHANGE_EVENT, handleResize);

    const lockScrollWithTransition = () => {
      handleLockScroll(isBig ? false : isOpen, isBig);
    };

    if (isOpen)
      dropdownRef.current?.addEventListener(
        'transitionend',
        lockScrollWithTransition
      );
    else {
      lockScrollWithTransition();
    }

    return () => {
      dropdownRef.current?.removeEventListener(
        'transitionend',
        lockScrollWithTransition
      );
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleResize);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener(
        BANNER_HEADER_OFFSET_CHANGE_EVENT,
        handleResize
      );
    };
  }, [updateElementPositions, isOpen, isBig, participant]);

  return { spaceBarRef, searchInputRef, dropdownRef };
};
